import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import campaignApi from "../api/campaignApi";

const EMPTY_FORM = {
  name: "",
  description: "",
  isActive: true,
};

const CampaignMaster = () => {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await campaignApi.getAll();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Unable to load campaigns.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const filteredCampaigns = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return campaigns;

    return campaigns.filter((item) => {
      return (
        String(item.name || "")
          .toLowerCase()
          .includes(q) ||
        String(item.description || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [campaigns, search]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setModalOpen(false);
  };

  const openCreateModal = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError("");
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = form.name.trim();

    if (!name) {
      setError("Campaign name is required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const payload = {
        name,
        description: form.description.trim(),
        isActive: Boolean(form.isActive),
      };

      if (editingId) {
        await campaignApi.update(editingId, payload);
      } else {
        await campaignApi.create(payload);
      }

      resetForm();
      await fetchCampaigns();
    } catch (err) {
      setError(err.message || "Unable to save campaign.");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (campaign) => {
    setEditingId(campaign._id);

    setForm({
      name: campaign.name || "",
      description: campaign.description || "",
      isActive: campaign.isActive !== false,
    });

    setError("");
    setModalOpen(true);
  };

  const handleToggle = async (campaign) => {
    try {
      setError("");

      await campaignApi.update(campaign._id, {
        isActive: campaign.isActive === false,
      });

      await fetchCampaigns();
    } catch (err) {
      setError(err.message || "Unable to update campaign.");
    }
  };

  const handleDelete = async (campaign) => {
    const ok = window.confirm(
      `Delete campaign "${campaign.name}"?\n\nExisting leads will keep their campaign name, but this campaign will no longer be available for new leads.`,
    );

    if (!ok) return;

    try {
      setError("");

      await campaignApi.remove(campaign._id);

      if (editingId === campaign._id) {
        resetForm();
      }

      await fetchCampaigns();
    } catch (err) {
      setError(err.message || "Unable to delete campaign.");
    }
  };

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div>
          <h2 style={s.title}>Campaign Master</h2>
          <p style={s.subtitle}>
            Create and manage campaigns used while creating CRM leads.
          </p>
        </div>

        <div style={s.topbarActions}>
          <button
            type="button"
            style={s.backBtn}
            onClick={() => navigate("/leads")}
          >
            ← Back to Leads
          </button>

          <button type="button" style={s.createBtn} onClick={openCreateModal}>
            + Create Campaign
          </button>
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      <div style={s.listCard}>
        <div style={s.listHeader}>
          <div>
            <div style={s.cardTitle}>Campaigns</div>
            <div style={s.count}>
              {campaigns.length} total campaign
              {campaigns.length === 1 ? "" : "s"}
            </div>
          </div>

          <input
            style={s.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaign..."
          />
        </div>

        {loading ? (
          <div style={s.empty}>Loading campaigns...</div>
        ) : filteredCampaigns.length === 0 ? (
          <div style={s.empty}>
            {campaigns.length === 0
              ? "No campaigns yet. Click Create Campaign to add one."
              : "No campaigns found for this search."}
          </div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Campaign</th>
                  <th style={s.th}>Description</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {filteredCampaigns.map((campaign) => (
                  <tr key={campaign._id}>
                    <td style={s.td}>
                      <strong>{campaign.name}</strong>
                    </td>

                    <td style={s.td}>{campaign.description || "—"}</td>

                    <td style={s.td}>
                      <span
                        style={{
                          ...s.badge,
                          ...(campaign.isActive !== false
                            ? s.activeBadge
                            : s.inactiveBadge),
                        }}
                      >
                        {campaign.isActive !== false ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td style={s.td}>
                      <div style={s.actionGroup}>
                        <button
                          type="button"
                          style={s.actionBtn}
                          onClick={() => handleEdit(campaign)}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          style={{
                            ...s.actionBtn,
                            color:
                              campaign.isActive !== false
                                ? "#d97706"
                                : "#16a34a",
                          }}
                          onClick={() => handleToggle(campaign)}
                        >
                          {campaign.isActive !== false
                            ? "Make Inactive"
                            : "Activate"}
                        </button>

                        <button
                          type="button"
                          style={{
                            ...s.actionBtn,
                            color: "#dc2626",
                          }}
                          onClick={() => handleDelete(campaign)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div
          style={s.overlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) {
              resetForm();
            }
          }}
        >
          <form
            style={s.modal}
            onSubmit={handleSubmit}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div style={s.modalHeader}>
              <div>
                <h3 style={s.modalTitle}>
                  {editingId ? "Edit Campaign" : "Create Campaign"}
                </h3>
                <p style={s.modalSubtitle}>
                  {editingId
                    ? "Update campaign details below."
                    : "Add a new campaign to Campaign Master."}
                </p>
              </div>

              <button
                type="button"
                style={s.closeBtn}
                onClick={resetForm}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <label style={s.label}>Campaign Name</label>
            <input
              style={s.input}
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }
              placeholder="Example: BIM 1"
              autoFocus
              required
            />

            <label style={s.label}>Description</label>
            <textarea
              style={{
                ...s.input,
                ...s.textarea,
              }}
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Optional campaign description"
            />

            <label style={s.checkboxRow}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    isActive: e.target.checked,
                  }))
                }
              />
              Active Campaign
            </label>

            <div style={s.infoBox}>
              Active campaigns will be available in the Lead campaign dropdown.
            </div>

            <div style={s.actions}>
              <button
                type="button"
                style={s.cancelBtn}
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="submit"
                style={{
                  ...s.saveBtn,
                  ...(saving ? s.disabledBtn : null),
                }}
                disabled={saving}
              >
                {saving
                  ? "Saving..."
                  : editingId
                    ? "Update Campaign"
                    : "Create Campaign"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

const s = {
  page: {
    padding: 20,
    fontFamily:
      "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
    color: "#1e293b",
  },

  topbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 18,
  },

  topbarActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },

  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    color: "#111a3d",
  },

  subtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: 13,
  },

  backBtn: {
    border: "1px solid #cbd5e1",
    background: "#fff",
    color: "#334155",
    borderRadius: 8,
    padding: "10px 14px",
    cursor: "pointer",
    fontWeight: 700,
  },

  createBtn: {
    border: "1px solid #111a3d",
    background: "#111a3d",
    color: "#fff",
    borderRadius: 8,
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: 700,
  },

  error: {
    marginBottom: 16,
    padding: "10px 14px",
    background: "#fee2e2",
    color: "#b91c1c",
    borderRadius: 8,
    fontSize: 13,
  },

  listCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(15,23,42,0.05)",
  },

  listHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    padding: 16,
    borderBottom: "1px solid #e2e8f0",
    flexWrap: "wrap",
  },

  cardTitle: {
    fontSize: 15,
    fontWeight: 800,
    color: "#111a3d",
  },

  count: {
    fontSize: 11.5,
    color: "#94a3b8",
    marginTop: 4,
  },

  search: {
    width: 260,
    maxWidth: "100%",
    padding: "9px 10px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    fontSize: 12.5,
    outline: "none",
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },

  th: {
    textAlign: "left",
    padding: "11px 13px",
    background: "#f8fafc",
    color: "#64748b",
    borderBottom: "1px solid #e2e8f0",
    fontSize: 11.5,
    whiteSpace: "nowrap",
  },

  td: {
    padding: "12px 13px",
    borderBottom: "1px solid #eef2f7",
    verticalAlign: "middle",
  },

  badge: {
    display: "inline-flex",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
  },

  activeBadge: {
    background: "#dcfce7",
    color: "#166534",
  },

  inactiveBadge: {
    background: "#f1f5f9",
    color: "#64748b",
  },

  actionGroup: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },

  actionBtn: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    padding: "3px 7px",
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
  },

  empty: {
    padding: 40,
    textAlign: "center",
    color: "#64748b",
    fontSize: 13,
  },

  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.48)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: 20,
  },

  modal: {
    width: "100%",
    maxWidth: 500,
    background: "#fff",
    borderRadius: 14,
    padding: 24,
    boxShadow: "0 22px 60px rgba(15,23,42,0.22)",
  },

  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
    marginBottom: 18,
  },

  modalTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
    color: "#111a3d",
  },

  modalSubtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: 12.5,
  },

  closeBtn: {
    border: "none",
    background: "#f1f5f9",
    color: "#475569",
    width: 32,
    height: 32,
    borderRadius: 8,
    fontSize: 20,
    lineHeight: 1,
    fontWeight: 700,
    cursor: "pointer",
    flexShrink: 0,
  },

  label: {
    display: "block",
    fontSize: 12,
    fontWeight: 700,
    color: "#475569",
    margin: "12px 0 5px",
  },

  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 11px",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
  },

  textarea: {
    minHeight: 90,
    resize: "vertical",
  },

  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    margin: "14px 0 10px",
    fontSize: 13,
    fontWeight: 600,
    color: "#475569",
  },

  infoBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "9px 10px",
    color: "#64748b",
    fontSize: 11.5,
    lineHeight: 1.5,
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 20,
  },

  cancelBtn: {
    border: "none",
    background: "#f1f5f9",
    color: "#334155",
    borderRadius: 8,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },

  saveBtn: {
    border: "none",
    background: "#111a3d",
    color: "#fff",
    borderRadius: 8,
    padding: "10px 14px",
    fontWeight: 700,
    cursor: "pointer",
  },

  disabledBtn: {
    opacity: 0.65,
    cursor: "not-allowed",
  },
};

export default CampaignMaster;
